import {
  CONTRAST_AA_LARGE,
  CONTRAST_AA_TEXT,
  contrastRatio,
  ensureContrast,
  parseHex,
} from "@repo/theme/a11y/contrast";

/**
 * Venue theming via CSS custom properties. Venue branding (colours, radius) is projected onto a
 * small, fixed set of `--rsv-*` variables scoped to the widget root; the shell's CSS reads them with
 * hard-coded fallbacks, so an absent or rejected value degrades to the platform default rather than
 * breaking the layout. Custom properties are the right seam: one write re-themes the whole tree, and
 * later the diner-booking-ui inherits the same variables for free.
 *
 * Values arrive from the (untrusted) public venue config, so each is validated against a strict
 * allowlist before it is set — a colour that is not a plain hex/rgb/hsl/keyword is dropped, never
 * written. (`setProperty` cannot inject new rules, but validating keeps the surface auditable.)
 *
 * Contrast fallback at theme resolution (features/accessibility.md R2): the brand primary drives BOTH
 * text/borders (slot + stepper + secondary controls) AND the filled-button surface, so a low-contrast
 * venue colour would fail AA on the widget's light surface. A HEX primary is passed through the SAME
 * `@repo/theme` `ensureContrast` the venue-site build uses (≥4.5:1 vs the resolved surface, hue held);
 * the accent (focus ring, a UI component) is floored at ≥3:1. Non-hex colours are used as-is — the
 * OKLCH search is undefined for them and the allowlist still owns them. The stored colour is untouched;
 * only the projected `--rsv-*` value is the adjusted shade. The widget logo is an image, so it always
 * shows the raw brand colour regardless.
 */
export interface VenueBranding {
  primaryColor?: string;
  onPrimaryColor?: string;
  accentColor?: string;
  surfaceColor?: string;
  radius?: string;
  fontFamily?: string;
}

const COLOR_RE =
  /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$|^(?:rgb|hsl)a?\([0-9.,%\s/]+\)$/;
const KEYWORD_RE = /^[a-z]{3,20}$/; // CSS colour keywords (transparent, white, …)
const RADIUS_RE = /^(?:0|[0-9]{1,3}(?:\.[0-9]+)?(?:px|rem|em)|9999px)$/;
// Font families: a comma-separated list of quoted/unquoted family names — no braces, no semicolons.
const FONT_RE = /^[a-zA-Z0-9 ,'"\-_]{1,120}$/;

const WIDGET_DEFAULT_SURFACE = "#ffffff";

function safeColor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return COLOR_RE.test(trimmed) || KEYWORD_RE.test(trimmed) ? trimmed : undefined;
}

function safe(value: string | undefined, re: RegExp): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return re.test(trimmed) ? trimmed : undefined;
}

/** The higher-contrast of near-black vs white to place ON a solid `base`. */
function onToneFor(base: string): string {
  return contrastRatio(base, "#ffffff") >= contrastRatio(base, "#141414") ? "#ffffff" : "#141414";
}

/**
 * Adjust a validated brand colour for AA against the surface, hue-preserving, ONLY when it is a hex we
 * can decompose (the OKLCH search's domain). rgb()/hsl()/keyword values pass through unchanged.
 */
function contrastSafe(color: string, surface: string, target: number): string {
  return parseHex(color) ? ensureContrast(color, surface, target) : color;
}

/** The validated `{ '--rsv-*': value }` map for a branding record (only present, valid keys). */
export function venueThemeVars(branding: VenueBranding | undefined | null): Record<string, string> {
  const vars: Record<string, string> = {};
  const set = (name: string, value: string | undefined) => {
    if (value) vars[name] = value;
  };

  const surfaceRaw = safeColor(branding?.surfaceColor);
  // The surface every ratio is computed against: the venue's own surface when it is a hex, else white.
  const contrastSurface = surfaceRaw && parseHex(surfaceRaw) ? surfaceRaw : WIDGET_DEFAULT_SURFACE;

  const primaryRaw = safeColor(branding?.primaryColor);
  const primary = primaryRaw
    ? contrastSafe(primaryRaw, contrastSurface, CONTRAST_AA_TEXT)
    : undefined;
  const adjusted = primary !== undefined && primary !== primaryRaw;

  set("--rsv-color-primary", primary);
  // When the primary was darkened for contrast, the venue's own on-primary may no longer be legible on
  // it: re-derive the max-contrast tone. An UNADJUSTED primary keeps the venue's provided on-primary.
  const onPrimary = adjusted && primary ? onToneFor(primary) : safeColor(branding?.onPrimaryColor);
  set("--rsv-color-on-primary", onPrimary);
  // Accent drives the focus ring (a UI component) — floor it at 3:1 against the surface.
  const accentRaw = safeColor(branding?.accentColor);
  set(
    "--rsv-color-accent",
    accentRaw ? contrastSafe(accentRaw, contrastSurface, CONTRAST_AA_LARGE) : undefined,
  );
  set("--rsv-color-surface", surfaceRaw);
  set("--rsv-radius", safe(branding?.radius, RADIUS_RE));
  set("--rsv-font", safe(branding?.fontFamily, FONT_RE));
  return vars;
}

/** Apply the validated branding variables onto an element's inline style. */
export function applyVenueTheme(
  element: HTMLElement,
  branding: VenueBranding | undefined | null,
): void {
  const vars = venueThemeVars(branding);
  for (const [name, value] of Object.entries(vars)) {
    element.style.setProperty(name, value);
  }
}
